import * as React from "react";
import { useMemo, useState } from "react";
import { styled } from "styled-components";
import { Row } from "common/components";
import useSelectedDataLake from "data-lake/hooks/useSelectedDataLake";
import useDataLakeTables from "data-lake/preview/storage-types/external/hooks/useDataLakeTables";
import { Spinner, ExpandableList, ExpandableListItem, IconButton, Icon } from "components/common";
import { Button } from "components/bootstrap";
import TableFieldList from "data-lake/preview/storage-types/external/TableFieldList";
import useUpdateTableFilter from "data-lake/preview/storage-types/external/hooks/useUpdateTableFilter";
import { WAHREHOUSE_PREVIEW_FORM_ID } from "data-lake/preview/storage-types/internal/SearchBar";
import useReexecuteSearch from "data-lake/preview/hooks/useReexecuteSearch";
const StyledRow = styled(Row)`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-right: 5px;
  flex: 0.4;
  max-width: 700px;
  min-width: 340px;

  && {
    padding: 7px 9px 6px;
    margin-bottom: 0;
  }
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 25px;
  width: 100%;
`;
const StyledExpandableList = styled(ExpandableList)`
  width: 100%;
  overflow: auto;
  flex: 1;
  margin-bottom: 0;
`;
const TableName = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover button {
    display: flex;
  }
`;
const UpdateTableFilterButton = styled(Button)`
  display: none;
  z-index: 1;
  right: 0;
  top: 5px;
  position: absolute;
`;
const StyledP = styled.p`
  margin-bottom: 5px;
`;
const Tables = styled.div`
  position: relative;

  &:hover button {
    display: flex;
  }
`;
const groupTables = (tableList) => {
  const catalogMap = /* @__PURE__ */ new Map();
  tableList.forEach((table) => {
    var _a, _b;
    const schemaMap = (_a = catalogMap.get(table.catalog)) != null ? _a : /* @__PURE__ */ new Map();
    const tables = (_b = schemaMap.get(table.schema)) != null ? _b : [];
    schemaMap.set(table.schema, [...tables, table]);
    catalogMap.set(table.catalog, schemaMap);
  });
  return [...catalogMap].map(([catalogName, schemaMap]) => ({
    catalogName,
    schemas: [...schemaMap].map(([schemaName, tables]) => ({ schemaName, tables }))
  }));
};
const initialExpanded = (groupedTables, selectedTable) => {
  if (selectedTable) {
    return {
      catalogs: selectedTable ? [selectedTable.catalog] : [],
      schemas: selectedTable ? [selectedTable.schema] : [],
      tables: selectedTable ? [selectedTable.id] : []
    };
  }
  if (groupedTables.length === 1) {
    return {
      catalogs: [groupedTables[0].catalogName],
      schemas: groupedTables[0].schemas.length === 1 ? [groupedTables[0].schemas[0].schemaName] : [],
      tables: []
    };
  }
  return { catalogs: [], schemas: [], tables: [] };
};
const List = ({ searchBarFormRef, tables }) => {
  var _a;
  const { dataLakeFieldsFetchQuery } = useSelectedDataLake();
  const selectedTable = tables == null ? void 0 : tables.find((table) => table.id === (dataLakeFieldsFetchQuery == null ? void 0 : dataLakeFieldsFetchQuery.tableId));
  const groupedTables = useMemo(() => groupTables(tables), [tables]);
  const [expanded, setExpanded] = useState(initialExpanded(groupedTables, selectedTable));
  const reexecuteSearch = useReexecuteSearch();
  const updateTableFilter = useUpdateTableFilter((_a = searchBarFormRef.current) == null ? void 0 : _a.setFieldValue);
  const handleExpand = (key, newValue) => {
    setExpanded((prev) => ({ ...prev, [key]: newValue }));
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledP, { className: "description" }, "Set a table as the active filter to select its fields. After changes", " ", /* @__PURE__ */ React.createElement(
    Button,
    {
      type: "submit",
      bsSize: "xsmall",
      bsStyle: "primary",
      disabled: false,
      onClick: reexecuteSearch,
      form: WAHREHOUSE_PREVIEW_FORM_ID
    },
    /* @__PURE__ */ React.createElement(Icon, { name: "search" }),
    "Re-execute"
  ), " ", "the search to see results."), /* @__PURE__ */ React.createElement(StyledExpandableList, { value: expanded.catalogs, onChange: (v) => handleExpand("catalogs", v) }, groupedTables.map(({ catalogName, schemas }) => /* @__PURE__ */ React.createElement(
    ExpandableListItem,
    {
      value: catalogName,
      key: catalogName,
      header: /* @__PURE__ */ React.createElement(React.Fragment, null, catalogName === (selectedTable == null ? void 0 : selectedTable.catalog) ? /* @__PURE__ */ React.createElement("b", null, catalogName) : catalogName)
    },
    /* @__PURE__ */ React.createElement(ExpandableList, { value: expanded.schemas, onChange: (v) => handleExpand("schemas", v) }, schemas.map(({ schemaName, tables: schemaTables }) => /* @__PURE__ */ React.createElement(
      ExpandableListItem,
      {
        key: schemaName,
        value: schemaName,
        header: schemaName === (selectedTable == null ? void 0 : selectedTable.schema) ? /* @__PURE__ */ React.createElement("b", null, schemaName) : schemaName
      },
      /* @__PURE__ */ React.createElement(ExpandableList, { value: expanded.tables, onChange: (v) => handleExpand("tables", v) }, schemaTables.map((table) => /* @__PURE__ */ React.createElement(Tables, { key: table.id }, (selectedTable == null ? void 0 : selectedTable.id) !== table.id && /* @__PURE__ */ React.createElement(
        UpdateTableFilterButton,
        {
          bsStyle: "primary",
          bsSize: "xsmall",
          onClick: (e) => {
            e.stopPropagation();
            updateTableFilter(table.id);
          }
        },
        "Update filter"
      ), /* @__PURE__ */ React.createElement(
        ExpandableListItem,
        {
          value: table.id,
          header: /* @__PURE__ */ React.createElement(TableName, null, (selectedTable == null ? void 0 : selectedTable.id) === table.id ? /* @__PURE__ */ React.createElement("b", null, table.name) : table.name)
        },
        /* @__PURE__ */ React.createElement(TableFieldList, { tableId: table.id, isExpanded: expanded.tables.includes(table.id) })
      ))))
    )))
  ))));
};
const TableList = ({ searchBarFormRef, onClose }) => {
  const { selectedDataLake } = useSelectedDataLake();
  const { data: tables, isLoading } = useDataLakeTables(selectedDataLake == null ? void 0 : selectedDataLake.id);
  return /* @__PURE__ */ React.createElement(StyledRow, { className: "content" }, /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement("h5", null, "Table List"), /* @__PURE__ */ React.createElement(IconButton, { name: "close", onClick: onClose, title: "Hide table list" })), isLoading ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading Tables..." }) : /* @__PURE__ */ React.createElement(List, { searchBarFormRef, tables }));
};
export default TableList;
